import { Renderer, type RenderOptions } from './Renderer';
import { RenderLoop } from './RenderLoop';
import type { InputManager } from '../input/InputManager';

interface FrameBufferSource {
  frameBuffer: Uint8Array;
  statusIcons: Record<string, boolean>;
}

export class SkeuomorphicUI {
  readonly canvas: HTMLCanvasElement;
  readonly renderer: Renderer;
  readonly renderLoop: RenderLoop;

  private screenConfig: { cols: number; rows: number } = { cols: 256, rows: 63 };
  private frameBufferSource: (() => FrameBufferSource | null) | null = null;
  private buttons: Array<{ id: string; x: number; y: number; w: number; h: number }> = [];
  private interfaceImage: ImageBitmap | null = null;
  private pressedButtons: Set<string> = new Set();
  private scale = 2;
  private resizeObserver: ResizeObserver | null = null;

  constructor(container: HTMLElement) {

    this.canvas = document.createElement('canvas');
    this.canvas.style.display = 'block';
    this.canvas.style.width = '100%';
    this.canvas.style.height = '100%';
    this.canvas.style.imageRendering = 'pixelated';
    container.appendChild(this.canvas);

    this.renderer = new Renderer(this.canvas);
    this.renderLoop = new RenderLoop(this.renderer);

    this.resizeObserver = new ResizeObserver(() => this.handleResize());
    this.resizeObserver.observe(container);
    this.handleResize();
  }

  bindInputManager(inputManager: InputManager): void {
    inputManager.attachAll(this.canvas);
  }

  setScreenConfig(config: { cols: number; rows: number }): void {
    this.screenConfig = config;
  }

  setFrameBufferSource(source: () => FrameBufferSource | null): void {
    this.frameBufferSource = source;
  }

  setButtons(buttons: Array<{ id: string; x: number; y: number; w: number; h: number }>): void {
    this.buttons = buttons;
  }

  setInterfaceImage(image: ImageBitmap): void {
    this.interfaceImage = image;
  }

  setPressedButtons(pressed: Set<string>): void {
    this.pressedButtons = pressed;
  }

  setScale(scale: number): void {
    this.scale = scale;
    this.handleResize();
  }

  start(): void {
    this.renderLoop.setFrameCallback(() => {
      const source = this.frameBufferSource?.() ?? null;
      if (!source) return null;

      const options: RenderOptions = {
        frameBuffer: source.frameBuffer,
        screenConfig: this.screenConfig,
        statusIcons: source.statusIcons,
        interfaceImage: this.interfaceImage,
        buttons: this.buttons,
        pressedButtons: this.pressedButtons,
        mode: 'skeuomorphic',
        scale: this.scale,
      };

      return options;
    });

    this.renderLoop.start();
  }

  stop(): void {
    this.renderLoop.stop();
  }

  destroy(): void {
    this.stop();

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    if (this.canvas.parentNode) {
      this.canvas.parentNode.removeChild(this.canvas);
    }
  }

  private handleResize(): void {
    const width = this.screenConfig.cols * 8 * this.scale;
    const height = this.screenConfig.rows * this.scale;
    this.renderer.setSize(width, height);
  }
}
